var _a, _b;

import { areCookiesAuthorized, combine, Observable, trackRuntimeError, canUseEventBridge, getEventBridge, startInternalMonitoring, RawReportType, initReportObservable, initConsoleObservable, ConsoleApiName, ErrorSource, getFileFromStackTraceString, startBatchWithReplica } from '@datadog/browser-core';
import { trackNetworkError } from '../domain/trackNetworkError';
import { StatusType } from '../domain/logger';
import { startLogsSessionManager, startLogsSessionManagerStub } from '../domain/logsSessionManager';
import { buildAssemble, getRUMInternalContext } from '../domain/assemble';
var LogStatusForApi = (_a = {}, _a[ConsoleApiName.log] = StatusType.info, _a[ConsoleApiName.debug] = StatusType.debug, _a[ConsoleApiName.info] = StatusType.info, _a[ConsoleApiName.warn] = StatusType.warn, _a[ConsoleApiName.error] = StatusType.error, _a);
var LogStatusForReport = (_b = {}, _b[RawReportType.cspViolation] = StatusType.error, _b[RawReportType.intervention] = StatusType.error, _b[RawReportType.deprecation] = StatusType.warn, _b);
export function startLogs(configuration, sender) {
  var internalMonitoring = startLogsInternalMonitoring(configuration);
  internalMonitoring.setExternalContextProvider(function () {
    var _a;

    return combine({
      session_id: (_a = session.findTrackedSession()) === null || _a === void 0 ? void 0 : _a.id
    }, getRUMInternalContext(), {
      view: {
        name: null,
        url: null,
        referrer: null
      }
    });
  });
  internalMonitoring.setTelemetryContextProvider(function () {
    var _a, _b, _c, _d, _e, _f;

    return {
      application: {
        id: (_a = getRUMInternalContext()) === null || _a === void 0 ? void 0 : _a.application_id
      },
      session: {
        id: (_b = session.findTrackedSession()) === null || _b === void 0 ? void 0 : _b.id
      },
      view: {
        id: (_d = (_c = getRUMInternalContext()) === null || _c === void 0 ? void 0 : _c.view) === null || _d === void 0 ? void 0 : _d.id
      },
      action: {
        id: (_f = (_e = getRUMInternalContext()) === null || _e === void 0 ? void 0 : _e.user_action) === null || _f === void 0 ? void 0 : _f.id
      }
    };
  });
  var rawErrorObservable = new Observable();

  if (configuration.forwardErrorsToLogs) {
    trackRuntimeError(rawErrorObservable);
    trackNetworkError(configuration, rawErrorObservable);
  }

  var consoleObservable = initConsoleObservable(configuration.forwardConsoleLogs);
  var reportObservable = initReportObservable(configuration.forwardReports);
  var session = areCookiesAuthorized(configuration.cookieOptions) && !canUseEventBridge() ? startLogsSessionManager(configuration) : startLogsSessionManagerStub(configuration);
  return doStartLogs(configuration, rawErrorObservable, consoleObservable, reportObservable, session, sender);
}

function startLogsInternalMonitoring(configuration) {
  var _a, _b;

  var internalMonitoring = startInternalMonitoring(configuration);

  if (canUseEventBridge()) {
    var bridge_1 = getEventBridge();
    internalMonitoring.monitoringMessageObservable.subscribe(function (message) {
      return bridge_1.send('internal_log', message);
    });
    internalMonitoring.telemetryEventObservable.subscribe(function (message) {
      return bridge_1.send('internal_telemetry', message);
    });
  } else {
    if (configuration.internalMonitoringEndpointBuilder) {
      var batch_1 = startBatchWithReplica(configuration, configuration.internalMonitoringEndpointBuilder, (_a = configuration.replica) === null || _a === void 0 ? void 0 : _a.internalMonitoringEndpointBuilder);
      internalMonitoring.monitoringMessageObservable.subscribe(function (message) {
        return batch_1.add(message);
      });
    }

    var monitoringBatch_1 = startBatchWithReplica(configuration, configuration.rumEndpointBuilder, (_b = configuration.replica) === null || _b === void 0 ? void 0 : _b.rumEndpointBuilder);
    internalMonitoring.telemetryEventObservable.subscribe(function (event) {
      return monitoringBatch_1.add(event);
    });
  }

  return internalMonitoring;
}

export function doStartLogs(configuration, rawErrorObservable, consoleObservable, reportObservable, sessionManager, sender) {
  var _a;

  var assemble = buildAssemble(sessionManager, configuration, reportRawError);
  var onLogEventCollected;

  if (canUseEventBridge()) {
    var bridge_2 = getEventBridge();

    onLogEventCollected = function onLogEventCollected(message) {
      return bridge_2.send('log', message);
    };
  } else {
    var batch_2 = startBatchWithReplica(configuration, configuration.logsEndpointBuilder, (_a = configuration.replica) === null || _a === void 0 ? void 0 : _a.logsEndpointBuilder);

    onLogEventCollected = function onLogEventCollected(message) {
      return batch_2.add(message);
    };
  }

  function reportRawError(error) {
    var messageContext = {
      date: error.startClocks.timeStamp,
      error: {
        kind: error.type,
        origin: error.source,
        stack: error.stack
      }
    };

    if (error.resource) {
      messageContext.http = {
        method: error.resource.method,
        status_code: error.resource.statusCode,
        url: error.resource.url
      };
    }

    sender.sendToHttp(error.message, messageContext, StatusType.error);
  }

  function reportConsoleLog(log) {
    var messageContext;

    if (log.api === ConsoleApiName.error) {
      messageContext = {
        error: {
          origin: ErrorSource.CONSOLE,
          stack: log.stack
        }
      };
    }

    sender.sendToHttp(log.message, messageContext, LogStatusForApi[log.api]);
  }

  function logReport(report) {
    var message = report.message;
    var messageContext;
    var logStatus = LogStatusForReport[report.type];

    if (logStatus === StatusType.error) {
      messageContext = {
        error: {
          kind: report.subtype,
          origin: ErrorSource.REPORT,
          stack: report.stack
        }
      };
    } else if (report.stack) {
      message += " Found in ".concat(getFileFromStackTraceString(report.stack));
    }

    sender.sendToHttp(message, messageContext, logStatus);
  }

  var rawErrorSubscription = rawErrorObservable.subscribe(reportRawError);
  var consoleSubscription = consoleObservable.subscribe(reportConsoleLog);
  var reportSubscription = reportObservable.subscribe(logReport);
  return {
    stop: function stop() {
      rawErrorSubscription.unsubscribe();
      consoleSubscription.unsubscribe();
      reportSubscription.unsubscribe();
    },
    send: function send(message, currentContext) {
      var contextualizedMessage = assemble(message, currentContext);

      if (contextualizedMessage) {
        onLogEventCollected(contextualizedMessage);
      }
    }
  };
}